import type { AbsUnit, Block, ChainLine, ExLine, Exercise, Line, Load } from './types';
import { findFmt, parseFmt, stripFmt } from './format';
import { mkBlock, mkChain, mkLine, mkSet, nid } from './content';

/* Текст → структура (CON-5). Работает только по кнопке AI — набранный текст сам не разбирается.
   Первый проход перед LLM: правила узнают обычную нотацию, остальное остаётся текстом.
   Принцип: строку отдаём упражнению, только если все её значимые слова покрыты названием или
   синонимом — лучше честный текст, чем правдоподобная подмена.
   ВНИМАНИЕ: \w и \b не видят кириллицу — только явные [а-яё]. */

export const norm = (s: string) =>
  s
    .toLowerCase()
    .replace(/[ёë]/g, 'е')
    .replace(/[^a-zа-я0-9 ]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

function sharedPrefix(a: string, b: string) {
  const L = Math.min(a.length, b.length);
  let i = 0;
  while (i < L && a[i] === b[i]) i++;
  return i >= Math.ceil(L * 0.6) ? i : 0;
}

const FILLER = new Set([
  'по', 'на', 'с', 'со', 'в', 'во', 'и', 'к', 'до', 'от', 'за', 'из', 'для', 'x', 'х', 'rpe', 'rir',
  'повт', 'повтор', 'повтора', 'повторов', 'повторения', 'повторений', 'раз', 'раза', 'подх', 'подход', 'подхода', 'подходов',
  'мин', 'минут', 'минуты', 'минута', 'сек', 'секунд', 'секунды', 'м', 'км', 'метр', 'метра', 'метров', 'кг', 'кал',
  'раунд', 'раунда', 'раундов', 'круг', 'круга', 'кругов',
]);

/** Индекс базы для сопоставления строк с упражнениями. Строится один раз на каталог. */
export class ExerciseMatcher {
  private cand: { e: Exercise; c: string[]; words: string[] }[];
  constructor(exercises: Exercise[]) {
    this.cand = exercises.map((e) => {
      const c = [e.ru, e.en, ...(e.aliases || [])].map(norm).filter(Boolean);
      return { e, c, words: [...new Set(c.flatMap((n) => n.split(' ')))] };
    });
  }

  /** Лучшее упражнение по названию или null */
  match(text: string): Exercise | null {
    const n = norm(text);
    if (!n) return null;
    const qt = n.split(' ').filter((t) => t.length >= 3);
    let best: Exercise | null = null;
    let score = 0;
    for (const { e, c } of this.cand) {
      let s = 0;
      for (const cand of c) {
        if (n === cand) {
          s = Math.max(s, 100 + cand.length);
          continue;
        }
        if (n.includes(cand)) {
          s = Math.max(s, 40 + cand.length);
          continue;
        }
        const ct = cand.split(' ').filter((t) => t.length >= 3);
        if (!ct.length || !qt.length) continue;
        let hit = 0;
        for (const w of ct) for (const q of qt) if (q === w || sharedPrefix(q, w) >= 4) { hit++; break; }
        if (hit) s = Math.max(s, hit * 6 * (hit / ct.length));
      }
      if (s > score) {
        score = s;
        best = e;
      }
    }
    return score >= 6 ? best : null;
  }

  /** Все значимые слова строки покрыты названием или синонимом упражнения */
  covered(text: string, e: Exercise): boolean {
    const entry = this.cand.find((x) => x.e.id === e.id);
    if (!entry) return false;
    const qt = norm(text)
      .split(' ')
      .filter((w) => w && !/^\d/.test(w) && !FILLER.has(w));
    return qt.every((q) => entry.words.some((w) => w === q || sharedPrefix(q, w) >= 4));
  }

  /** Подсказки при наборе: по вхождению и началу слов */
  suggest(text: string, limit = 8): Exercise[] {
    const n = norm(text);
    if (!n) return [];
    const qt = n.split(' ');
    const scored: { e: Exercise; s: number }[] = [];
    for (const { e, c } of this.cand) {
      let s = 0;
      for (const cand of c) {
        if (cand.startsWith(n)) s = Math.max(s, 100 - cand.length / 10);
        else if (cand.includes(n)) s = Math.max(s, 60 - cand.length / 10);
        else if (qt.every((q) => cand.split(' ').some((w) => w.startsWith(q)))) s = Math.max(s, 40 - cand.length / 10);
      }
      if (s > 0) scored.push({ e, s: s + (e.own ? 5 : 0) });
    }
    return scored
      .sort((a, b) => b.s - a.s)
      .slice(0, limit)
      .map((x) => x.e);
  }
}

const NOL = '(?![а-яёa-z])';
const NUM = '(\\d+(?:[.,]\\d+)?)';
const RG = '(?:\\s*[-–—]\\s*(\\d+(?:[.,]\\d+)?))?';
/* Окончания — явным [а-яё]*: в прототипе \w здесь ломал «10 повторов» и «500 метров». */
const UNITS: [AbsUnit, RegExp][] = [
  ['кг', new RegExp(NUM + RG + '\\s*(?:кг|kg)' + NOL, 'i')],
  ['мин', new RegExp(NUM + RG + '\\s*(?:мин[а-яё]*|min)' + NOL, 'i')],
  ['сек', new RegExp('(\\d+)' + RG + '\\s*(?:сек[а-яё]*|sec)' + NOL, 'i')],
  ['кал', new RegExp('(\\d+)' + RG + '\\s*(?:кал[а-яё]*|cal)' + NOL, 'i')],
  ['повт', new RegExp('(\\d+)' + RG + '\\s*(?:повт[а-яё]*|reps?|раз[а]?)' + NOL, 'i')],
  ['м', new RegExp('(\\d+)' + RG + '\\s*(?:метр[а-яё]*|м|m)' + NOL, 'i')],
];
export const RE_PCT = /@?\s*(\d{1,3}(?:[.,]\d)?)(?:\s*[-–—]\s*(\d{1,3}(?:[.,]\d)?))?\s*%/;
/* «4×8-10» — подходы × диапазон повторов; «4×10/10» — на каждую ногу или руку поочерёдно;
   «3-4×10/10» — диапазон подходов */
const RE_SCHEME = /\d+(?:\s*[-–]\s*\d+)?\s*[x×хХ*]\s*\d+(?:\s*[-–/]\s*\d+)*|\d+\s*[-–]\s*\d+\s*[x×хХ*](?!\s*\d)|\d+(?:\s*[-/]\s*\d+)+/;
const RE_SETS = /\d+(?:\s*[-–]\s*\d+)?\s*[x×хХ]/;
/* «3 по 5» — так пишут в тетради */
const RE_PO = /(\d+)\s+по\s+(\d+(?:\s*[-–]\s*\d+)?)(?![\d.,]*\s*(?:кг|kg|%|м|сек|мин))/i;

const num = (s: string) => parseFloat(s.replace(',', '.'));

interface Params {
  scheme: string;
  load: Load | null;
  rest: string;
}

/** Вытащить из строки нагрузку и схему; rest — что осталось (название и мусор). */
export function extractParams(L: string): Params {
  let rest = ' ' + L.replace(/\*/g, '×') + ' ';
  let load: Load | null = null;
  let repsUnit: { from: string; to?: string } | null = null;

  const mp = rest.match(RE_PCT);
  if (mp) {
    load = { kind: 'pct', from: num(mp[1]), ...(mp[2] ? { to: num(mp[2]) } : {}) };
    rest = rest.replace(mp[0], ' ');
  }
  for (const [u, re] of UNITS) {
    const m = rest.match(re);
    if (!m) continue;
    if (u === 'повт') repsUnit = { from: m[1], to: m[2] };
    else if (!load) load = { kind: 'abs', unit: u, from: num(m[1]), ...(m[2] ? { to: num(m[2]) } : {}) };
    else continue;
    rest = rest.replace(m[0], ' ');
    if (u !== 'повт') break;
  }
  let scheme = '';
  const po = rest.match(RE_PO);
  const ms = !po && (rest.match(RE_SCHEME) || rest.match(RE_SETS));
  if (po) {
    scheme = po[1] + '×' + po[2].replace(/\s|–/g, (c) => (c === '–' ? '-' : ''));
    rest = rest.replace(po[0], ' ');
  } else if (ms) {
    scheme = ms[0].replace(/\s/g, '').replace(/[xхХ*]/g, '×').replace(/–/g, '-');
    rest = rest.replace(ms[0], ' ');
  } else {
    const lead = rest.match(/^\s*(\d+)\s+(?=\D)/);
    /* Голое число без «x»/«по»/единицы (поле схемы и так про число повторов/раундов) —
       не наткнувшись на него тут, строка уходила в txt и стирала уже введённую нагрузку. */
    const bare = !lead && rest.trim().match(/^\d+$/);
    if (lead) {
      scheme = lead[1];
      rest = rest.replace(lead[0], ' ');
    } else if (bare) {
      scheme = bare[0];
      rest = rest.replace(bare[0], ' ');
    }
  }
  /* «10 повторов» без схемы — это схема «10», а не единица нагрузки */
  if (repsUnit) {
    const r = repsUnit.to ? repsUnit.from + '-' + repsUnit.to : repsUnit.from;
    scheme = scheme ? (/×$/.test(scheme) ? scheme + r : scheme) : r;
  }
  return { scheme, load, rest };
}

export type ParsedLine =
  | { type: 'ok'; src: string; item: Line; ex?: Exercise }
  | { type: 'fmt'; src: string; fmt: ReturnType<typeof parseFmt> }
  | { type: 'raw'; src: string };

/** Позиция двоеточия, которое не стоит между цифрами (как в «1:30»); -1 — нет такого */
function colonAt(s: string) {
  for (let i = s.indexOf(':'); i >= 0; i = s.indexOf(':', i + 1)) if (!(/\d/.test(s[i - 1] || '') && /\d/.test(s[i + 1] || ''))) return i;
  return -1;
}

export class WorkoutParser {
  constructor(private m: ExerciseMatcher) {}

  /** Разобрать одну строку. */
  line(src: string): ParsedLine {
    const L = src.trim();
    const f = parseFmt(L);
    if (f && !RE_SCHEME.test(L)) return { type: 'fmt', src: L, fmt: f };
    const ch = this.chain(L);
    if (ch) return { type: 'ok', src: L, item: ch };
    const { scheme, load, rest } = extractParams(L);
    const e = this.m.match(rest);
    if (!e || !this.m.covered(rest, e)) return { type: 'raw', src: L };
    const item: ExLine = mkLine(e.id);
    if (scheme) item.scheme = scheme;
    if (load) item.load = load;
    /* После схемы и нагрузки остались цифры — запись сложнее («60×5, 70×5, 80×3×3»):
       упражнение узнано, всё после названия — текстом при упражнении (CON-19). */
    if (/\d/.test(rest)) {
      const mp2 = L.match(/^([^\d@%(]+?)\s+(?=[\d@%(])(.+)$/);
      if (mp2) {
        item.txt = mp2[2].trim();
        delete item.scheme;
        delete item.load;
      }
    }
    return { type: 'ok', src: L, item, ex: e };
  }

  /** Связка (CON-23): «Взятие на грудь + Фронтальный присед + Толчок: 90 кг — 3×(1+1+1)» или «взятие 1 + присед 1 + толчок 1 90 кг». */
  chain(L: string): ChainLine | null {
    let s = String(L || '');
    let sets: number | null = null;
    let reps: string[] | null = null;
    const m = s.match(/(?:(\d+)\s*[x×хХ*]\s*)?\(\s*(\d+(?:\s*\+\s*\d+)+)\s*\)/);
    if (m) {
      sets = m[1] ? +m[1] : null;
      reps = m[2].split('+').map((x) => x.trim());
      s = s.replace(m[0], ' ');
    }
    s = s.replace(/:/g, ' ').replace(/\s[—–-]\s/g, ' ');
    const parts = s
      .split(/\s\+\s/)
      .map((t) => t.replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (parts.length < 2) return null;
    const c = mkChain();
    for (let t of parts) {
      const mp = t.match(RE_PCT);
      const mk = t.match(/(\d+(?:[.,]\d+)?)(?:\s*[-–—]\s*(\d+(?:[.,]\d+)?))?\s*кг(?![а-яё])/i);
      if (mp) {
        if (!c.load) c.load = { kind: 'pct', from: num(mp[1]), ...(mp[2] ? { to: num(mp[2]) } : {}) };
        t = t.replace(mp[0], ' ');
      } else if (mk) {
        if (!c.load) c.load = { kind: 'abs', unit: 'кг', from: num(mk[1]), ...(mk[2] ? { to: num(mk[2]) } : {}) };
        t = t.replace(mk[0], ' ');
      }
      const mr = t.match(/\s(\d+)\s*$/);
      if (mr) t = t.slice(0, mr.index);
      const name = t.trim();
      const e = this.m.match(name);
      if (!e || !this.m.covered(name, e)) return null;
      c.parts.push({ id: nid(), exId: e.id, reps: mr ? mr[1] : undefined });
    }
    if (reps && reps.length === c.parts.length) c.parts.forEach((p, k) => (p.reps = reps![k]));
    if (sets) c.sets = sets;
    return c;
  }

  /** Заголовок сета: «Сет 3 круга», «3 раза:», «Сет ×4, отдых 90 сек», перечень после двоеточия. */
  setHead(text: string): { rounds: number; rest: string; list: string[] } | null {
    let L = String(text || '').trim();
    if (!L || /на\s*время|for\s*time|amrap|emom|табат|tabata|кажд/i.test(L)) return null;
    let list = '';
    /* Двоеточие перечня, но не времени «1:30». Без ретроспективной проверки (?<!…) — её нет в Safari до iOS 16.4,
       а с ней весь бандл не разбирается и приложение остаётся белым */
    const ci = colonAt(L);
    if (ci >= 0) {
      list = L.slice(ci + 1).trim();
      L = L.slice(0, ci).trim();
    }
    let rest = '';
    const mr = L.match(/[,;]?\s*отдых\s*(?:между\s*круг[а-яё]*\s*)?(\d+:\d{2}|\d+(?:[.,]\d+)?\s*(?:сек|мин|с|м)[а-яё]*\.?)\s*$/i);
    if (mr) {
      rest = mr[1].trim();
      L = L.slice(0, mr.index).trim();
    }
    L = L.replace(/[,;.\s]+$/, '');
    const m =
      L.match(/^(?:(?:супер)?сет\s*[x×х]?\s*)?(\d{1,2})\s*(?:раза?|круг[а-яё]*|раунд[а-яё]*)$/i) ||
      L.match(/^(?:супер)?сет(?:\s*[x×х]\s*(\d{1,2}))?$/i);
    if (!m) return null;
    const items = list
      ? list
          .split(/\s*;\s*|,\s+/)
          .map((t) => t.replace(/^[-–—•*]\s*/, '').trim())
          .filter(Boolean)
      : [];
    return { rounds: m[1] ? +m[1] : 3, rest, list: items };
  }

  private lineOrRaw(t: string): Line {
    const r = this.line(t);
    return r.type === 'ok' ? r.item : mkLine(null, t);
  }

  /** Текст → блоки. Граница — пустая строка; заголовок — строка с форматом, короткая без цифр или с двоеточием. */
  blocks(text: string): Block[] {
    type Draft = { title: string; items: Block['items']; src: string[] };
    const out0: Draft[] = [];
    let cur: Draft | null = null;
    let ss: { bullets: boolean | null; set: ReturnType<typeof mkSet> } | null = null;
    const open = (title: string) => {
      cur = { title, items: [], src: [] };
      out0.push(cur);
      ss = null;
    };
    const isHeader = (L: string) => L.length <= 42 && (/:$/.test(L) || !/\d/.test(L));
    for (const line of String(text).split('\n')) {
      const L = line.trim();
      if (!L) {
        cur = null;
        ss = null;
        continue;
      }
      const bullet = /^[-–—•*]\s*/.test(L);
      const body = L.replace(/^[-–—•*]\s*/, '');
      if (!bullet && findFmt(L) && !RE_SCHEME.test(L)) {
        open(L.replace(/:$/, ''));
        cur!.src.push(L);
        continue;
      }
      const sh = bullet ? null : this.setHead(L);
      if (sh) {
        if (!cur) open('');
        const set = mkSet(sh.list.map((t) => this.lineOrRaw(t)), sh.rounds, sh.rest);
        cur!.items.push(set);
        cur!.src.push(L);
        ss = sh.list.length ? null : { bullets: null, set };
        continue;
      }
      if (ss) {
        if (ss.bullets === null) ss.bullets = bullet;
        if ((ss.bullets && !bullet) || /:$/.test(L)) ss = null;
      }
      if (!ss && !bullet && isHeader(L)) {
        open(L.replace(/:$/, ''));
        cur!.src.push(L);
        continue;
      }
      if (!cur) open('');
      const it = this.lineOrRaw(body);
      if (ss) ss.set.items.push(it);
      else cur!.items.push(it);
      cur!.src.push(L);
    }
    /* Заголовок без содержимого — просто строка текстом в предыдущем блоке. */
    const out: Draft[] = [];
    for (const b of out0) {
      if (b.items.length) {
        out.push(b);
        continue;
      }
      if (!b.title) continue;
      const it = mkLine(null, b.src[0] || b.title);
      const prev = out[out.length - 1];
      if (prev) prev.items.push(it);
      else out.push({ title: '', items: [it], src: b.src });
    }
    return out.map((d) => {
      const f = findFmt(d.title);
      const b = mkBlock({ title: f ? stripFmt(d.title) : d.title, items: d.items });
      if (f) {
        const { src: _s, ...fmt } = f;
        b.fmt = fmt;
        b.kind = 'complex';
      }
      /* Сет из одного участника — не сет */
      b.items = b.items.flatMap((y) => (y.t === 'set' && y.items.length < 2 ? y.items : [y]));
      return b;
    });
  }
}

/* ─── Поля строки в конструкторе ─── */

/** Поле «Схема» (понимает и нагрузку: «5x3 80%»). Всё, что не разложилось, — txt. */
export function parseSchemeField(v: string): { scheme: string; load: Load | null } | { txt: string } {
  const src = String(v || '').trim();
  if (!src) return { scheme: '', load: null };
  const { scheme, load, rest } = extractParams(src);
  const left = rest.replace(/(^|\s)(по|на|с|@|x|×)(?=\s|$)/gi, ' ');
  if (/[^\s,;·]/.test(left)) return { txt: src };
  return { scheme, load };
}

const LOAD_SUFFIX: [RegExp, AbsUnit | '%'][] = [
  [/^(%|проц)/i, '%'],
  [/^(кал|ка|cal)/i, 'кал'],
  [/^(кг|kg|к|k)\.?$/i, 'кг'],
  [/^(мин|ми|min)/i, 'мин'],
  [/^(м|m|метр)/i, 'м'],
  /* «с», а не «см»: «короб 50 см» — не секунды */
  [/^(сек|с\.?$|sec|s\.?$)/i, 'сек'],
  [/^(повт|раз|rep)/i, 'повт'],
];

/** Поле «Нагрузка»: «80%», «100кг», «1:30», «70-80», «60..70 кг». unit=null — единица из текущего выбора. */
export function parseLoadField(raw: string): { empty: true } | { text: string } | { from: number; to?: number; unit: AbsUnit | '%' | null } {
  const s = String(raw || '')
    .trim()
    .replace(/\s*(?:[-–—]|\.\.)\s*$/, '');
  if (!s) return { empty: true };
  const mt = s.match(/^(\d+):(\d{2})$/);
  if (mt) return { from: +mt[1] * 60 + +mt[2], unit: 'сек' };
  const hitOf = (suf: string) => (suf ? LOAD_SUFFIX.find(([re]) => re.test(suf)) : null);
  const mr = s.replace(/,/g, '.').match(/^(\d+(?:\.\d+)?)\s*(?:[-–—]|\.\.)\s*(\d+(?:\.\d+)?)\s*(.*)$/);
  if (mr) {
    const a = +mr[1];
    const b = +mr[2];
    const suf = mr[3].trim();
    const hit = hitOf(suf);
    if (suf && !hit) return { text: s };
    const unit = hit ? hit[1] : null;
    return a === b ? { from: a, unit } : { from: Math.min(a, b), to: Math.max(a, b), unit };
  }
  const m = s.replace(',', '.').match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
  if (!m) return { text: s };
  const suf = m[2].trim();
  if (!suf) return { from: +m[1], unit: null };
  const hit = hitOf(suf);
  return hit ? { from: +m[1], unit: hit[1] } : { text: s };
}
